import React from 'react';
import { isNil } from 'lodash';

import { StateChip } from '../../ActionStateChip';
import ClampedText from '../../ClampedText';
import { ACTION_NEEDS_ATTENTION, QUESTION_STATE_APPEARANCE, QUESTION_STATE_CHIP } from '../../constants';
import RejectionReason from '../../RejectionReason';
import { TEXTS } from '../../texts';
import type { AskState, ConductorQuestion } from '../../types';
import {
  activityCardElementId,
  askRejectionReason,
  askStateLabel,
  logEntryElementId,
  ownWordsAnswer,
  questionOutcome,
} from '../../utils';
import ActivityCard from '../ActivityCard';
import type { QuestionRow } from '../types';

const ANSWER_IN_LOG = 'Answer this in the conductor log';

/**
 * The line a closed question ends on. The chip already names who closed it, so an option picked is
 * quoted, the handler's own words are said to be theirs, and a rejection ends on its note, under a
 * label of its own; any other closure says what became of the question, as the log's record does.
 */
const closedLine = (state: AskState, question: ConductorQuestion): string | null => {
  const response = question.human_response;
  if (state === 'answered' && response?.gesture === 'answered') {
    const ownWords = ownWordsAnswer(question);

    return isNil(ownWords) ? `“${response.answer}”` : TEXTS.YOUR_ANSWER_LINE(ownWords);
  }
  if (state === 'rejected') return null;

  return questionOutcome(state, question);
};

interface QuestionCardProps {
  row: QuestionRow;
  /** Brings the question's log entry into view and marks it selected. */
  onSelectLogEntry: (row: QuestionRow) => void;
  /** Rings the card: the log last took the reader here. */
  isSelected: boolean;
  onDeselect: () => void;
}

/**
 * A question Clive raised, as a pointer into the log. Nothing here answers it: the options and the
 * answer box are in the log, beside the reasoning that asks the question, so the card's only control
 * is the jump there.
 */
const QuestionCard: React.FC<QuestionCardProps> = ({ row, onSelectLogEntry, isSelected, onDeselect }) => {
  const { key, ask, state, logEventId } = row;
  const line = closedLine(state, ask);

  return (
    <ActivityCard
      kind="question"
      appearance={QUESTION_STATE_APPEARANCE[state]}
      title={ask.ask}
      subtitle={ask.summary}
      status={<StateChip label={askStateLabel(state, ask)} className={QUESTION_STATE_CHIP[state]} />}
      createdAt={ask.created_at}
      onSelect={() => onSelectLogEntry(row)}
      controls={logEventId ? logEntryElementId(logEventId) : undefined}
      // The log holds the answer box only while the question waits; a closed one is only shown there.
      selectLabel={ACTION_NEEDS_ATTENTION[state] ? ANSWER_IN_LOG : undefined}
      elementId={activityCardElementId(key)}
      isSelected={isSelected}
      onDeselect={onDeselect}
    >
      <ClampedText>{ask.description}</ClampedText>
      {line ? <ClampedText>{line}</ClampedText> : null}
      <RejectionReason reason={askRejectionReason(state, ask)} />
    </ActivityCard>
  );
};

export default QuestionCard;
